// AA offers — product-page upsell (<aa-offer-upsell>), loaded as a module by
// blocks/aa-offer-upsell.liquid.
//
// A slideshow under the buy buttons, one product per slide, auto-advancing until
// the shopper touches it. A product with
// sizes opens the theme's quick-add dialog (the same one Shop the look uses),
// by clicking the card's own hidden native quick-add. A one-variant product
// (a cap) is added straight away through /cart/add.js with the cart sections
// attached and a CartLinesUpdateEvent (@shopify/events) — the same path
// product-form.js uses —
// so the drawer opens exactly as it does for Add to cart.
//
// Measuring the card: every add from it, direct or through the dialog, carries a
// hidden `_aa_offer` line property (look / colourway / cap), so take rates can be
// read off orders. The dialog's form belongs to the theme, so the property is
// slipped into it at submit time (see armDialog). Clicks and completed adds also
// go to GA4 (`aa_upsell_click`, `aa_upsell_add`) and Clarity (same event names,
// plus an `aa_upsell` session tag holding the offer).
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DialogCloseEvent } from '@theme/dialog';

const AUTO_MS = 4000;

const root = () => window.Shopify?.routes?.root || '/';

// GA4 (gtag from the Google & YouTube app) and Clarity (its app embed) are both
// on the page as globals. Either may be missing or blocked; never let that break an add.
function track(name, offer, productId, flow) {
  try {
    window.gtag?.('event', name, { aa_offer: offer, aa_flow: flow, item_id: productId });
  } catch (error) {}
  try {
    window.clarity?.('set', 'aa_upsell', offer);
    window.clarity?.('event', name);
  } catch (error) {}
}

// Offer of the card that last opened the quick-add dialog, until it closes.
let armed = null;

function armDialog(offer, productId) {
  armed = { offer, productId };
  document
    .getElementById('quick-add-dialog')
    ?.addEventListener(DialogCloseEvent.eventName, () => (armed = null), { once: true });
}

// Another quick-add (cart strip, Shop the look) opening the dialog is not ours.
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element && event.target.closest('.quick-add__button--choose');
    if (button && !button.closest('aa-offer-upsell')) armed = null;
  },
  true
);

// Capture phase on window, so this runs before product-form reads the form (the
// theme's own submit handling also listens in capture on document). The input is
// added at submit rather than on open because a size change re-renders the form.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!armed || !(form instanceof HTMLFormElement) || !form.closest('#quick-add-dialog')) return;
    let input = form.querySelector('input[name="properties[_aa_offer]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_aa_offer]';
      form.append(input);
    }
    input.value = armed.offer;

    const { offer, productId } = armed;
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (update) =>
        update.promise?.then(
          (result) => !result?.detail?.didError && track('aa_upsell_add', offer, productId, 'dialog'),
          () => {}
        ),
      { once: true }
    );
  },
  true
);

async function addItems(items, source, sourceElement) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);

  // Theme 4.x: the drawer and cart sections listen for CartLinesUpdateEvent and
  // re-render from the sections carried on its resolved promise.
  const deferred = CartLinesUpdateEvent.createPromise();
  sourceElement.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 })),
      promise: deferred.promise,
    })
  );

  const response = await fetch(`${root()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sectionIds.join(',') }),
  });
  const json = await response.json();
  const cart = await (await fetch(`${root()}cart.js`)).json();
  const failed = !response.ok || Boolean(json.status);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source,
      sourceId: sourceElement.id || source,
      itemCount: items.length,
      sections: failed ? undefined : json.sections,
      didError: failed,
    },
  });
  if (failed) {
    throw new Error(json.description || json.message || 'Could not add to cart');
  }
}

if (!customElements.get('aa-offer-upsell')) {
  customElements.define(
    'aa-offer-upsell',
    class extends HTMLElement {
      #picks = [];
      #reloadTimer;
      #autoTimer;
      #inView = false;
      #touched = false;
      #observer;
      #active = 0;

      connectedCallback() {
        document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);

        const list = this.querySelector('[data-aa-items]');
        list?.addEventListener('scroll', this.#onScroll, { passive: true });
        // Any hand on the slideshow ends auto-advance for this page view.
        for (const type of ['pointerdown', 'focusin']) {
          this.addEventListener(type, () => (this.#touched = true));
        }

        this.#observer = new IntersectionObserver(
          ([entry]) => {
            this.#inView = entry.isIntersecting;
          },
          { threshold: 0.5 }
        );
        this.#observer.observe(this);

        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.#autoTimer = setInterval(this.#tick, AUTO_MS);
        }
        this.#load();
      }

      disconnectedCallback() {
        document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
        clearTimeout(this.#reloadTimer);
        clearInterval(this.#autoTimer);
        this.#observer?.disconnect();
      }

      // Any cart change (this add, the main Add to cart, the drawer) can move
      // the offer line or put a slide's product in the cart, so re-read.
      #onCartUpdate = (event) => {
        // The event fires as the add starts; its promise settles when it's done.
        const refresh = () => {
          clearTimeout(this.#reloadTimer);
          this.#reloadTimer = setTimeout(() => this.#load(), 100);
        };
        if (event?.promise) event.promise.then(refresh, refresh);
        else refresh();
      };

      #tick = () => {
        const slides = this.querySelectorAll('.aa-ou__card').length;
        if (slides < 2 || this.#touched || !this.#inView || document.hidden) return;
        // Not behind the cart drawer or the quick-add dialog.
        if (document.querySelector('dialog[open]')) return;
        this.#goTo((this.#active + 1) % slides);
      };

      #goTo(index) {
        const list = this.querySelector('[data-aa-items]');
        list?.scrollTo({ left: index * list.clientWidth, behavior: 'smooth' });
      }

      #onScroll = (event) => {
        const list = event.currentTarget;
        const index = Math.round(list.scrollLeft / (list.clientWidth || 1));
        if (index !== this.#active) this.#setActive(index);
      };

      #setActive(index) {
        this.#active = index;
        this.querySelectorAll('.aa-ou__dot').forEach((dot, i) => {
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.#renderClaim();
      }

      async #fetchItems(url) {
        try {
          const response = await fetch(url);
          if (!response.ok) return null;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          return html.querySelector('.aa-ou__items');
        } catch (error) {
          return null;
        }
      }

      async #load() {
        const productId = this.dataset.productId;
        const cardsOf = (items) =>
          items
            ? [...items.querySelectorAll('.aa-ou__card')].filter(
                (card) => card.dataset.productId !== productId && !card.hasAttribute('data-in-cart')
              )
            : [];

        const picks = await this.#fetchItems(
          `${this.dataset.recsUrl}?section_id=aa-offer-upsell-items&product_id=${productId}&limit=10&intent=complementary`
        );
        this.#picks = cardsOf(picks);
        this.#render();
      }

      // The first four picks, in the order set in Search & Discovery. The offer
      // label only tags the add for reporting: a cap, another colourway of this
      // product (same custom.colour_group), or a look.
      #slides() {
        const group = this.dataset.group;
        const offerOf = (card) => {
          if (card.dataset.type === 'Cap') return 'cap';
          if (group && card.dataset.group === group) return 'colourway';
          return 'look';
        };
        return this.#picks.slice(0, 4).map((card) => [card, offerOf(card)]);
      }

      #render() {
        const list = this.querySelector('[data-aa-items]');
        const dots = this.querySelector('[data-aa-dots]');
        if (!list || !dots) return;

        const slides = this.#slides();
        list.replaceChildren(
          ...slides.map(([template, offer]) => {
            const card = template.cloneNode(true);
            card.dataset.offer = offer;
            const button = card.querySelector('[data-aa-add]');
            // The photo is a link to the product; anywhere else on the row
            // (title, price, the space between, the plus) adds. The theme's
            // product card sends any click inside it to the product page
            // unless it carries data-no-navigation (product-card.js).
            card.querySelector('product-card')?.setAttribute('data-no-navigation', '');
            card.addEventListener('click', (event) => {
              const target = event.target instanceof Element ? event.target : null;
              if (!target || target.closest('.aa-ou__media, .aa-ou__qa')) return;
              if (button.disabled) return;
              this.#add(event, card, button);
            });
            return card;
          })
        );
        dots.replaceChildren(
          ...slides.map((_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'aa-ou__dot';
            dot.setAttribute('aria-label', `Product ${index + 1} of ${slides.length}`);
            dot.addEventListener('click', () => this.#goTo(index));
            return dot;
          })
        );
        dots.hidden = slides.length < 2;
        this.hidden = slides.length === 0;

        list.scrollLeft = 0;
        this.#setActive(0);
      }

      // One line: "Get" + every live offer (window.AAOffers.offers, Theme
      // settings → AA Offers) that this product + the slide's product reach on
      // their own, e.g. "Get free shipping & 10% off". The cart is not read.
      // Thresholds come converted to the visitor's currency (AAOffers.cents),
      // so prices and thresholds are both in presentment money.
      // ponytail: main price is the variant selected at page load; sizes share
      // a price today. Listen for variant changes if that ever stops holding.
      #renderClaim() {
        const claim = this.querySelector('[data-aa-claim]');
        if (!claim) return;
        const card = this.querySelectorAll('.aa-ou__card')[this.#active];

        let text = '';
        if (card && this.dataset.claims === 'true' && window.AAOffers) {
          const pair = (Number(this.dataset.mainPrice) || 0) + (Number(card.dataset.price) || 0);
          const rewards = window.AAOffers.offers
            .filter((offer) => offer.live)
            .filter((offer) => {
              const min = window.AAOffers.cents(offer.key);
              return min !== null && pair >= min;
            })
            .map((offer) => offer.reward);
          if (rewards.length) text = (this.dataset.claim || 'Get [reward]').replaceAll('[reward]', rewards.join(' & '));
        }

        claim.textContent = text;
        claim.hidden = !text;
      }

      async #add(event, card, button) {
        event.preventDefault();
        const error = card.querySelector('[data-aa-error]');
        if (error) error.hidden = true;

        const { offer, productId } = card.dataset;
        const multi = card.dataset.multi === 'true';
        track('aa_upsell_click', offer, productId, multi ? 'dialog' : 'direct');

        // Sizes to choose: hand over to the theme's quick-add dialog.
        if (multi) {
          armDialog(offer, productId);
          card.querySelector('.quick-add__button--choose')?.click();
          return;
        }

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        try {
          await addItems(
            [{ id: Number(card.dataset.variantId), quantity: 1, properties: { _aa_offer: card.dataset.offer } }],
            'aa-offer-upsell',
            this
          );
          track('aa_upsell_add', offer, productId, 'direct');
        } catch (err) {
          if (error) {
            error.textContent = err.message;
            error.hidden = false;
          }
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }
  );
}
